import { isNotNullOrUndefined } from '@microsoft/applicationinsights-core-js';
import {
  FluiditeTauxOccupationValues,
  GetFluiditeRatesResult,
  MenuDto,
  RestaurantDto,
  RestaurantSousTypeValues,
  RestaurantTypeValues,
} from '@timechef/core/api';
import { ensureDate, getDayOfWeek } from '@timechef/core/utils';
import { endOfDay, isWithinInterval } from 'date-fns';
import { FluiditeDetails, FluiditeRate, Menu, Restaurant } from './restaurants.models';

export const map_RestaurantDto_Restaurant = (dto: RestaurantDto): Restaurant => {
  const restaurant: Restaurant = {
    closedAt: dto.closedAt ? ensureDate(dto.closedAt) : undefined,
    openedAt: dto.openedAt ? ensureDate(dto.openedAt) : undefined,
    closedFrom: dto.closedFrom ? ensureDate(dto.closedFrom) : undefined,
    closedTo: dto.closedTo ? ensureDate(dto.closedTo) : undefined,
    hasFluidite: dto.hasFluidite,
    hasMenu: dto.hasMenu,
    fluidite: FluiditeTauxOccupationValues.Na,
    id: dto.id,
    isOpen: computedRestaurantOpened(dto),
    isExceptionallyClosed: computedRestaurantExceptionallyClosed(dto),
    openedDays: dto.openedDays,
    title: dto.title,
    type: dto.type,
    sousType: dto.sousType,
    imageUrl: dto.imageUrl,
    animationId: dto.animation?.id,
    surveyCampaign: dto.surveyCampaign,
    description: dto.description,
    typeLabel: dto.typeLabel,
    allowOrder: dto.allowOrder,
    allowOrderHistory: dto.allowOrderHistory,
    hasExternalOrderHistory: dto.hasExternalOrderHistory,
    isExternalServiceATable: dto.isExternalServiceATable,
    orderSettings: dto.orderSettings,
    contact: dto.contact,
    allowComments: dto.allowComments,
    isExternalCameraAllowed: dto.isExternalCameraAllowed,
    // Filled from other API calls
    menus: [],
    fluiditeDetails: undefined,
    surveyCampaignDetails: undefined,
    displaySettings: dto.displaySettings,
  };
  return restaurant;
};

export const map_getMenu_MenuDto = (dto: MenuDto, restaurant: Restaurant): Menu => {
  let pickupLimitDate: Date | undefined = undefined;

  switch (restaurant.type) {
    case RestaurantTypeValues.Scorp:
      switch (restaurant.sousType) {
        case RestaurantSousTypeValues.Scorp:
          pickupLimitDate = ensureDate(dto.date);
          break;
        case RestaurantSousTypeValues.AntiGaspi:
          pickupLimitDate = restaurant.closedAt ? ensureDate(restaurant.closedAt) : undefined;
          break;
      }
  }

  return { ...dto, timedOut: false, pickupLimitDate };
};

export const map_GetFluiditeResult_FluiditeDetails = (result: GetFluiditeRatesResult): FluiditeDetails => {
  return {
    rates: result.rates.map((dto, i) => {
      const startsAt = ensureDate(dto.startsAt);

      const fluidite: FluiditeRate = {
        startsAt: startsAt,
        endsAt: ensureDate(dto.endsAt),
        currentRate: dto.currentRate,
        currentWeekRate: dto.currentWeekRate,
        lastWeekRate: dto.lastWeekRate,
        showStartLabel: startsAt.getMinutes() === 0,
        showEndLabel: i === result.rates.length - 1,
      };
      return fluidite;
    }),
    capacity: result.capacity,
    remainingSeats: result.remainingSeats,
  };
};

export const computedRestaurantOpened = (restaurant: Restaurant | RestaurantDto) => {
  const now = new Date();
  const { openedAt, closedAt, openedDays, closedFrom, closedTo } = restaurant;

  if (!isNotNullOrUndefined(openedAt) && !isNotNullOrUndefined(closedAt)) return false;

  // Fermeture exceptionnelle
  if (closedFrom && closedTo && isWithinInterval(now, { start: ensureDate(closedFrom), end: ensureDate(closedTo) })) {
    return false;
  }
  // Jour ouverture
  if (openedDays) {
    const day = getDayOfWeek(now);
    if (!openedDays.includes(day)) return false;
  }
  // Horaire d'ouverture
  return isWithinInterval(now, { start: ensureDate(openedAt!), end: ensureDate(closedAt!) });
};

export const computedRestaurantExceptionallyClosed = (restaurant: Restaurant | RestaurantDto) => {
  const now = new Date();
  const { closedFrom, closedTo } = restaurant;

  return !!closedFrom && !!closedTo && isWithinInterval(now, { start: ensureDate(closedFrom), end: endOfDay(ensureDate(closedTo)) });
};

export const isCartRetrievalEnabled = (restaurant: Restaurant | undefined | null): boolean => {
  if (!restaurant) return false;
  return restaurant.type === RestaurantTypeValues.Scorp && restaurant.sousType === RestaurantSousTypeValues.Scorp;
};
